/**
 * A geometria do cartaz da vitrine — proporções, tamanhos e a largura.
 *
 * ## Por que este arquivo existe separado de `vitrine-settings.ts`
 *
 * Porque **a tela do aluno precisa da conta, e não pode pagar pelo schema**.
 * `vitrine-settings.ts` importa `zod`; importar `larguraDaCapa` de lá levava o
 * zod inteiro para dentro do bundle do aluno — medido: o pedaço de entrada
 * saltou de 307,9 kB para 370,2 kB por causa de uma função de dez linhas que não
 * valida nada.
 *
 * É a mesma razão que `lib/api.ts` do aluno já declara em cima do `import type`:
 * o bundle que abre no 4G do celular de quem comprou um curso não deve carregar
 * uma biblioteca de validação para saber a largura de uma imagem.
 *
 * Aqui não há `import` nenhum, de propósito, e `vitrine-settings.ts` reexporta
 * tudo — então continua havendo **uma** implementação, e quem já importava de lá
 * não muda uma linha.
 */

/**
 * As proporções, na ordem em que a tela as oferece.
 *
 * ## `3/4` é retrato; `4/3` é paisagem — e os dois existem
 *
 * A confusão custa uma vitrine inteira, então fica escrita: aqui a razão é
 * **largura/altura**, como no CSS. `4/3` é mais largo que alto — é a foto de
 * paisagem, e continua servindo a quem usa capa horizontal. `3/4` é mais alto
 * que largo: é pôster, como `2/3`, só que menos alongado.
 *
 * `3/4` abre a lista porque é o padrão (ver `configuracaoDaVitrine.capa`), e
 * porque a ordem da lista é a ordem em que a tela desenha as opções.
 */
export const PROPORCOES_DE_CAPA = ['3/4', '2/3', '16/9', '4/3', '1/1'] as const;
export type ProporcaoDeCapa = (typeof PROPORCOES_DE_CAPA)[number];

/**
 * A mesma proporção, no formato que o CSS quer em `aspect-ratio`.
 *
 * ## Por que isto passou a existir
 *
 * Porque já havia uma segunda cópia dele: um `Record<string, string>` local, no
 * topo de `apps/aluno/src/componentes/Vitrine.tsx`, com as quatro proporções
 * escritas à mão e um `?? '2 / 3'` de segurança. Enquanto a lista não mudava, as
 * duas cópias concordavam. Ao acrescentar `3/4`, a cópia de lá deixou de
 * conhecer a proporção **padrão** do produto — e o efeito não é um erro: é um
 * cartaz desenhado em `2/3` com a largura calculada para `3/4`, que ninguém
 * reporta porque parece só um pouco errado.
 *
 * A lista canônica é esta. Quem desenhar cartaz lê daqui, e uma proporção nova
 * passa a alcançar as duas pontas por existir, e não por alguém lembrar.
 */
export const ASPECTO_DA_CAPA: Record<ProporcaoDeCapa, string> = {
  '3/4': '3 / 4',
  '2/3': '2 / 3',
  '16/9': '16 / 9',
  '4/3': '4 / 3',
  '1/1': '1 / 1',
};

/** O `aspect-ratio` desta proporção. Desconhecida cai no padrão do produto. */
export function aspectoDaCapa(proporcao: string): string {
  return ASPECTO_DA_CAPA[proporcao as ProporcaoDeCapa] ?? ASPECTO_DA_CAPA['3/4'];
}

/**
 * O tamanho das capas — o segundo eixo, que faltava.
 *
 * ## Por que tamanho e proporção são dois eixos, e não cinco layouts
 *
 * Porque é o que a medição diz. Na referência que o dono usa, quatro dos cinco
 * "layouts" têm a **mesma largura** (372px); o que muda entre eles é a altura —
 * ou seja, a proporção — e um par pequeno/grande (372 contra 307). Os cinco nomes
 * dela são, na prática, o produto de dois eixos com um caso especial de catálogo.
 *
 * Modelar isso como cinco layouts nomeados faria "vertical grande" e "horizontal
 * grande" serem valores sem parentesco, e acrescentar uma proporção nova
 * exigiria inventar dois nomes. Dois eixos multiplicam-se sozinhos.
 *
 * ## Da ÁREA, e agora também de cada prateleira
 *
 * A versão anterior deste comentário dizia que escolher por prateleira era "o
 * passo seguinte, se ele pedir". Ele pediu. O que a área grava continua sendo o
 * padrão de toda faixa; o que uma prateleira grava vale só para ela.
 *
 * A herança mora em `prateleira-aparencia.ts`, e o motivo de ela morar lá está
 * escrito lá: **os dois eixos herdam separado**, e "não mexi" é `null`, nunca o
 * valor da área copiado para dentro da prateleira.
 */
export const TAMANHOS_DE_CAPA = ['pequeno', 'medio', 'grande'] as const;
export type TamanhoDeCapa = (typeof TAMANHOS_DE_CAPA)[number];

/**
 * A largura do cartão — a conta que estava escondida na vitrine do aluno.
 *
 * Ela morava num `Record` dentro de `apps/aluno`, o que fazia a tela do tutor
 * escolher uma proporção sem ter como dizer que tamanho ela vira. Agora as duas
 * pontas leem daqui: o aluno para desenhar, o painel para mostrar a prévia e
 * nomear a opção em pixels.
 *
 * ## Por que `clamp`, e por que cada proporção tem a sua
 *
 * O alvo não é uma largura, é uma **contagem**: pouco mais de dois cartazes no
 * celular (o terceiro cortado é o que convida a arrastar) e cinco ou seis no
 * computador. Um número fixo acerta um dos dois e erra o outro.
 *
 * E a largura é o que se escolhe, mas é a **altura** que se compara: um 16/9 com
 * a largura de um 2/3 fica com um terço da altura dele, e a prateleira de
 * miniaturas parece erro de carregamento ao lado da de cartazes. Cada faixa é a
 * largura que dá aproximadamente a mesma altura de cartão.
 *
 * ## De onde saem os três tamanhos
 *
 * `medio` é exatamente o que existia — trocar o padrão de todas as áreas no ar
 * por causa de um pedido de um dono seria mudar a vitrine de quem não pediu.
 * `grande` é ~19% da janela contra os 18,2% do maior da referência, que é a
 * queixa que originou isto: o dele era maior que o nosso. `pequeno` existe
 * porque quem tem quarenta cursos quer ver mais de seis por linha.
 */
export function larguraDaCapa(
  proporcao: ProporcaoDeCapa,
  tamanho: TamanhoDeCapa = 'medio',
): string {
  const { min, vw, max } = faixaDaCapa(proporcao, tamanho);
  return `clamp(${min}px, ${vw.toFixed(1)}vw, ${max}px)`;
}

/** A mesma `clamp`, em números — para quem precisa da conta, e não da string. */
export function faixaDaCapa(
  proporcao: ProporcaoDeCapa,
  tamanho: TamanhoDeCapa = 'medio',
): { min: number; vw: number; max: number } {
  const base: Record<string, [number, number, number]> = {
    '2/3': [150, 16, 300],
    /* Entre o `2/3` e o `1/1`, porque a proporção está entre os dois: um pôster
     * `3/4` com a largura de um `2/3` fica 12 pontos mais baixo, e a faixa
     * ficaria visivelmente menor que a que estava no ar. Um fio mais largo
     * devolve a altura. */
    '3/4': [158, 17, 316],
    '4/3': [184, 20, 356],
    '1/1': [170, 18, 324],
    '16/9': [228, 25, 440],
  };
  const fator: Record<TamanhoDeCapa, number> = { pequeno: 0.82, medio: 1, grande: 1.2 };
  const [min, vw, max] = base[proporcao] ?? base['2/3']!;
  const f = fator[tamanho];
  return { min: Math.round(min * f), vw: vw * f, max: Math.round(max * f) };
}

/**
 * O `sizes` do cartaz — a mesma `clamp`, traduzida para o que o `<img>` entende.
 *
 * ## Por que não `sizes="clamp(150px, 16vw, 300px)"` direto
 *
 * Porque o `sizes` é lido **antes** do CSS, pelo pré-carregador, e a gramática
 * dele aceita `<length>` com funções matemáticas só nos navegadores recentes. Um
 * `sizes` que o navegador não entende não degrada para "quase certo": ele
 * degrada para **100vw**, que é a largura da janela inteira — e o cartaz de
 * 300px passaria a pedir o derivado de 1920. O defeito seria invisível em quem
 * testou e caro em quem não.
 *
 * As duas quebras saem da própria `clamp`, e por isso não podem divergir dela:
 * abaixo de `min/vw*100` a largura é o piso; acima de `max/vw*100` é o teto;
 * entre as duas é a fração da janela. É a mesma conta que o CSS faz, escrita na
 * linguagem que o pré-carregador fala.
 *
 * `2/3` · `medio` → `(max-width: 937px) 150px, (max-width: 1875px) 16.0vw, 300px`
 */
export function sizesDaCapa(
  proporcao: ProporcaoDeCapa,
  tamanho: TamanhoDeCapa = 'medio',
): string {
  const { min, vw, max } = faixaDaCapa(proporcao, tamanho);
  const ateOPiso = Math.floor((min / vw) * 100);
  const ateOTeto = Math.floor((max / vw) * 100);
  return `(max-width: ${ateOPiso}px) ${min}px, (max-width: ${ateOTeto}px) ${vw.toFixed(1)}vw, ${max}px`;
}

/**
 * O `sizes` do herói.
 *
 * Ele sangra na largura da janela em toda largura medida (§18.7, V8), então a
 * resposta é uma constante — e uma constante com nome é o que impede alguém de
 * escrever `100vw` num JSX e ninguém mais achar de onde saiu.
 */
export const SIZES_DO_HEROI = '100vw';

